import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../services/api';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import { Badge, Button, Card, Input } from '../components/ui';

const buildEditState = (user) => ({
  role: user.role || 'client',
  planTier: user.planTier || 'basic',
  clientTier: user.clientTier || 'foundation',
  subscriptionPlan: user.subscription?.plan || 'none',
  subscriptionStatus: user.subscription?.status || 'none',
  subscriptionBillingCycle: user.subscription?.billingCycle || 'none',
  clientSlots: user.subscription?.clientSlots || 0
});

const AdminTiers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [editState, setEditState] = useState({});
  const [savingIds, setSavingIds] = useState([]);
  const [expandedUser, setExpandedUser] = useState(null);

  // Complimentary access modal state
  const [compUser, setCompUser] = useState(null);
  const [compPlan, setCompPlan] = useState('foundation');
  const [compDurationType, setCompDurationType] = useState('months');
  const [compDurationAmount, setCompDurationAmount] = useState(1);
  const [compReason, setCompReason] = useState('');
  const [compSaving, setCompSaving] = useState(false);

  const filteredParams = useMemo(() => {
    const params = {};
    if (search) params.search = search;
    if (role) params.role = role;
    return params;
  }, [search, role]);

  const loadUsers = async (params = {}) => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await adminService.getUsers(params);
      const list = response.data.data.users || [];
      setUsers(list);
      const nextEditState = {};
      list.forEach((user) => {
        nextEditState[user._id] = buildEditState(user);
      });
      setEditState(nextEditState);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load users.'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleFilter = (event) => {
    event.preventDefault();
    loadUsers(filteredParams);
  };

  const handleEditChange = (userId, field, value) => {
    setEditState((prev) => {
      const current = prev[userId] || buildEditState({});
      const next = { ...current, [field]: value };
      if (field === 'role') {
        if (value === 'clinician' || value === 'clergy') {
          next.planTier = current.planTier || 'enterprise';
        }
        if (value === 'client') {
          next.clientTier = current.clientTier || 'foundation';
        }
      }
      return { ...prev, [userId]: next };
    });
  };

  const handleSubscriptionChange = (userId, field, value) => {
    setEditState((prev) => {
      const current = prev[userId] || buildEditState({});
      const next = { ...current, [field]: value };
      return { ...prev, [userId]: next };
    });
  };

  const handleSaveSubscription = async (user) => {
    const state = editState[user._id];
    if (!state) return;

    if (!window.confirm('Update this user\'s subscription tier? A notification will be sent.')) {
      return;
    }

    setSavingIds((prev) => [...new Set([...prev, user._id])]);
    setMessage(null);

    try {
      const payload = {};
      
      if (state.subscriptionPlan !== (user.subscription?.plan || 'none')) {
        payload.plan = state.subscriptionPlan;
      }
      if (state.subscriptionStatus !== (user.subscription?.status || 'none')) {
        payload.status = state.subscriptionStatus;
      }
      if (state.subscriptionBillingCycle !== (user.subscription?.billingCycle || 'none')) {
        payload.billingCycle = state.subscriptionBillingCycle;
      }
      if (state.clientSlots !== (user.subscription?.clientSlots || 0)) {
        payload.clientSlots = state.clientSlots;
      }

      if (Object.keys(payload).length === 0) {
        setMessage({ type: 'warning', text: 'No subscription changes to save.' });
        return;
      }

      await adminService.updateUserSubscription(user._id, payload);
      setMessage({ type: 'success', text: 'Subscription updated and user notified.' });
      setExpandedUser(null);
      await loadUsers(filteredParams);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update subscription.'
      });
    } finally {
      setSavingIds((prev) => prev.filter((id) => id !== user._id));
    }
  };

  const openCompModal = (user) => {
    setCompUser(user);
    // Sensible default plan based on user role
    const defaultPlan = user.role === 'clinician'
      ? 'clinician_practitioner'
      : user.role === 'coach'
        ? 'coach_starter'
        : user.role === 'clergy'
          ? 'legacy_clergy'
          : 'foundation';
    setCompPlan(defaultPlan);
    setCompDurationType('months');
    setCompDurationAmount(1);
    setCompReason('');
  };

  const closeCompModal = () => {
    setCompUser(null);
    setCompReason('');
  };

  const handleGrantComplimentary = async () => {
    if (!compUser) return;

    const durationLabel = compDurationType === 'lifetime'
      ? 'lifetime (permanent)'
      : `${compDurationAmount} ${compDurationType}`;

    if (!window.confirm(`Grant ${compUser.firstName} ${compUser.lastName} complimentary ${durationLabel} access to the "${compPlan}" plan? They will receive an email notification.`)) {
      return;
    }

    setCompSaving(true);
    setMessage(null);

    try {
      const payload = {
        plan: compPlan,
        duration: {
          type: compDurationType,
          ...(compDurationType !== 'lifetime' ? { amount: compDurationAmount } : {})
        },
        reason: compReason.trim() || undefined
      };

      const response = await adminService.grantComplimentaryAccess(compUser._id, payload);
      setMessage({ type: 'success', text: response.data.message || 'Complimentary access granted.' });
      closeCompModal();
      await loadUsers(filteredParams);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to grant complimentary access.'
      });
    } finally {
      setCompSaving(false);
    }
  };

  const handleSave = async (user) => {
    const state = editState[user._id];
    if (!state) return;

    if (!window.confirm('Update this user\'s role or tier access?')) {
      return;
    }

    setSavingIds((prev) => [...new Set([...prev, user._id])]);
    setMessage(null);

    try {
      const payload = { role: state.role };
      if (state.role === 'clinician' || state.role === 'clergy') {
        payload.planTier = state.planTier;
      }
      if (state.role === 'client') {
        payload.clientTier = state.clientTier;
      }
      await adminService.updateUserAccess(user._id, payload);
      setMessage({ type: 'success', text: 'Access updated.' });
      await loadUsers(filteredParams);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update access.'
      });
    } finally {
      setSavingIds((prev) => prev.filter((id) => id !== user._id));
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading tier management..." />;
  }

  return (
    <>
      <Navigation />
      <main className="mx-auto max-w-[1920px] px-6 py-10">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <Badge variant="primary" size="sm">Admin Console</Badge>
            <h2 className="mt-3 text-3xl font-display font-semibold text-neutral-900">Tier Management</h2>
            <p className="mt-2 text-neutral-600">Adjust role access and tier assignments without billing.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" onClick={() => navigate('/admin')}>
              Back to Dashboard
            </Button>
          </div>
        </div>

        {message && (
          <div
            className={`mb-6 rounded-soft border p-3 text-sm ${
              message.type === 'success'
                ? 'border-success-200 bg-success-50 text-success-700'
                : 'border-error-200 bg-error-50 text-error-700'
            }`}
          >
            {message.text}
          </div>
        )}

        <Card>
          <form onSubmit={handleFilter} className="grid gap-4 md:grid-cols-3">
            <Input
              label="Search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name, email, username"
              fullWidth
            />
            <label className="text-sm text-neutral-700">
              <span className="mb-2 block font-medium">Role</span>
              <select
                value={role}
                onChange={(event) => setRole(event.target.value)}
                className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              >
                <option value="">All roles</option>
                <option value="client">Client</option>
                <option value="clinician">Clinician</option>
                <option value="clergy">Clergy</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <div className="flex items-end">
              <Button type="submit" className="w-full">Apply Filters</Button>
            </div>
          </form>
        </Card>

        <Card className="mt-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-display font-semibold text-neutral-800">User Access & Subscription</h3>
            <Badge variant="secondary">{users.length} users</Badge>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[1100px] text-left text-sm">
              <thead className="border-b border-neutral-200 text-neutral-500">
                <tr>
                  <th className="py-3 pr-4 font-medium">Name</th>
                  <th className="py-3 pr-4 font-medium">Email</th>
                  <th className="py-3 pr-4 font-medium">Role</th>
                  <th className="py-3 pr-4 font-medium">Tier</th>
                  <th className="py-3 pr-4 font-medium">Subscription</th>
                  <th className="py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {users.map((user) => {
                  const state = editState[user._id] || buildEditState(user);
                  const isSaving = savingIds.includes(user._id);
                  const subscriptionPlan = user.subscription?.plan || 'none';
                  const subscriptionStatus = user.subscription?.status || 'none';
                  const isFreePlan = ['none', 'free_trial', 'legacy_client', 'legacy_clinician', 'legacy_clergy', 'legacy_coach'].includes(subscriptionPlan);
                  const isExpanded = expandedUser === user._id;

                  return (
                    <React.Fragment key={user._id}>
                      <tr className="hover:bg-neutral-50">
                        <td className="py-3 pr-4 text-neutral-800">
                          {user.firstName} {user.lastName}
                        </td>
                        <td className="py-3 pr-4 text-neutral-600">{user.email}</td>
                        <td className="py-3 pr-4">
                          <select
                            value={state.role}
                            onChange={(event) => handleEditChange(user._id, 'role', event.target.value)}
                            className="w-full rounded-soft border border-neutral-200 px-2 py-1 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                          >
                            <option value="client">Client</option>
                            <option value="clinician">Clinician</option>
                            <option value="clergy">Clergy</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td className="py-3 pr-4">
                          {(state.role === 'clinician' || state.role === 'clergy') && (
                            <select
                              value={state.planTier}
                              onChange={(event) => handleEditChange(user._id, 'planTier', event.target.value)}
                              className="w-full rounded-soft border border-neutral-200 px-2 py-1 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                            >
                              <option value="basic">Basic (5 clients)</option>
                              <option value="pro">Pro (25 clients)</option>
                              <option value="enterprise">Enterprise (Unlimited)</option>
                            </select>
                          )}
                          {state.role === 'client' && (
                            <select
                              value={state.clientTier}
                              onChange={(event) => handleEditChange(user._id, 'clientTier', event.target.value)}
                              className="w-full rounded-soft border border-neutral-200 px-2 py-1 text-sm text-neutral-700 outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                            >
                              <option value="foundation">Foundation</option>
                              <option value="guided">Guided</option>
                            </select>
                          )}
                          {state.role === 'admin' && (
                            <span className="text-neutral-500">—</span>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-sm">
                          <div className="flex items-center gap-2">
                            <Badge variant={isFreePlan ? 'secondary' : 'primary'} size="sm">
                              {isFreePlan ? 'Free' : 'Paid'}
                            </Badge>
                            {subscriptionStatus !== 'none' && (
                              <Badge variant="outline" size="sm">
                                {subscriptionStatus}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-3 text-right space-x-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setExpandedUser(isExpanded ? null : user._id)}
                          >
                            {isExpanded ? '−' : '+'} Subscription
                          </Button>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleSave(user)}
                            disabled={isSaving}
                          >
                            {isSaving ? 'Saving...' : 'Save'}
                          </Button>
                        </td>
                      </tr>
                      
                      {isExpanded && (
                        <tr className="bg-neutral-50">
                          <td colSpan={6} className="py-4 px-4">
                            <Card className="bg-white">
                              <h4 className="text-sm font-semibold text-neutral-900 mb-4">Update Subscription</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <label className="text-sm text-neutral-700">
                                  <span className="block font-medium mb-1">Plan</span>
                                  <select
                                    value={state.subscriptionPlan}
                                    onChange={(e) => handleSubscriptionChange(user._id, 'subscriptionPlan', e.target.value)}
                                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                                  >
                                    <option value="none">— None (Free)</option>
                                    <option value="foundation">Foundation ($29/mo)</option>
                                    <option value="guided">Guided ($19/mo)</option>
                                    <option value="recovery">Companion ($9/mo)</option>
                                    <option value="clinician_practitioner">Practitioner (20 slots)</option>
                                    <option value="clinician_practice">Practice (50 slots)</option>
                                    <option value="clinician_org">Organization (75 slots)</option>
                                    <option value="institutional">Institutional</option>
                                    <option value="legacy_client">Legacy Client (Free)</option>
                                    <option value="legacy_clinician">Legacy Clinician (Free)</option>
                                    <option value="legacy_clergy">Legacy Clergy (Free)</option>
                                    <option value="legacy_coach">Legacy Coach (Free)</option>
                                    <option value="voucher">Voucher</option>
                                    <option value="admin">Admin</option>
                                  </select>
                                </label>
                                
                                <label className="text-sm text-neutral-700">
                                  <span className="block font-medium mb-1">Status</span>
                                  <select
                                    value={state.subscriptionStatus}
                                    onChange={(e) => handleSubscriptionChange(user._id, 'subscriptionStatus', e.target.value)}
                                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                                  >
                                    <option value="none">None</option>
                                    <option value="active">Active</option>
                                    <option value="past_due">Past Due</option>
                                    <option value="canceled">Canceled</option>
                                    <option value="trialing">Trialing</option>
                                    <option value="paused">Paused</option>
                                  </select>
                                </label>

                                <label className="text-sm text-neutral-700">
                                  <span className="block font-medium mb-1">Billing Cycle</span>
                                  <select
                                    value={state.subscriptionBillingCycle}
                                    onChange={(e) => handleSubscriptionChange(user._id, 'subscriptionBillingCycle', e.target.value)}
                                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                                  >
                                    <option value="none">None</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="annual">Annual</option>
                                  </select>
                                </label>

                                <label className="text-sm text-neutral-700">
                                  <span className="block font-medium mb-1">Client Slots</span>
                                  <input
                                    type="number"
                                    min="0"
                                    max="999"
                                    value={state.clientSlots}
                                    onChange={(e) => handleSubscriptionChange(user._id, 'clientSlots', parseInt(e.target.value) || 0)}
                                    className="w-full rounded-soft border border-neutral-200 px-3 py-2 text-sm outline-none transition-gentle focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                                  />
                                </label>
                              </div>

                              <div className="mt-4 flex flex-wrap gap-2">
                                <Button
                                  size="sm"
                                  variant="primary"
                                  onClick={() => handleSaveSubscription(user)}
                                  disabled={isSaving}
                                >
                                  {isSaving ? 'Saving...' : 'Update Subscription'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => openCompModal(user)}
                                >
                                  🎁 Grant Complimentary Access
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setExpandedUser(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </Card>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-neutral-500">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </main>

      {/* Complimentary Access Modal */}
      {compUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/50 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h3 className="text-xl font-display font-semibold text-neutral-900">
                  🎁 Grant Complimentary Access
                </h3>
                <p className="mt-1 text-sm text-neutral-600">
                  {compUser.firstName} {compUser.lastName} &middot; {compUser.email}
                </p>
              </div>
              <button
                onClick={closeCompModal}
                className="text-neutral-400 hover:text-neutral-600"
                disabled={compSaving}
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Plan</label>
                <select
                  value={compPlan}
                  onChange={(e) => setCompPlan(e.target.value)}
                  className="w-full rounded-soft border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                >
                  <optgroup label="Client Plans">
                    <option value="foundation">Foundation (normally $29/mo)</option>
                    <option value="guided">Guided (normally $19/mo)</option>
                    <option value="recovery">Companion (normally $9/mo)</option>
                    <option value="legacy_client">Legacy Client (Free tier)</option>
                  </optgroup>
                  <optgroup label="Clinician Plans">
                    <option value="clinician_practitioner">Practitioner (20 slots)</option>
                    <option value="clinician_practice">Practice (50 slots)</option>
                    <option value="clinician_org">Organization (75 slots)</option>
                    <option value="legacy_clinician">Legacy Clinician (Free tier)</option>
                  </optgroup>
                  <optgroup label="Coach Plans">
                    <option value="coach_starter">Coach Starter</option>
                    <option value="coach_professional">Coach Professional</option>
                    <option value="coach_organization">Coach Organization</option>
                    <option value="legacy_coach">Legacy Coach (Free tier)</option>
                  </optgroup>
                  <optgroup label="Other">
                    <option value="legacy_clergy">Legacy Clergy (Free tier)</option>
                    <option value="institutional">Institutional</option>
                    <option value="voucher">Voucher (Grant-funded)</option>
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Duration</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setCompDurationType('months')}
                    className={`rounded-soft border-2 px-3 py-2 text-sm font-medium transition-colors ${
                      compDurationType === 'months'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'
                    }`}
                  >
                    Months
                  </button>
                  <button
                    type="button"
                    onClick={() => setCompDurationType('years')}
                    className={`rounded-soft border-2 px-3 py-2 text-sm font-medium transition-colors ${
                      compDurationType === 'years'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'
                    }`}
                  >
                    Years
                  </button>
                  <button
                    type="button"
                    onClick={() => setCompDurationType('lifetime')}
                    className={`rounded-soft border-2 px-3 py-2 text-sm font-medium transition-colors ${
                      compDurationType === 'lifetime'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'
                    }`}
                  >
                    Lifetime
                  </button>
                </div>
              </div>

              {compDurationType !== 'lifetime' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Number of {compDurationType}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={compDurationType === 'months' ? 240 : 20}
                    value={compDurationAmount}
                    onChange={(e) => setCompDurationAmount(parseInt(e.target.value, 10) || 1)}
                    className="w-full rounded-soft border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  />
                  <p className="mt-1 text-xs text-neutral-500">
                    {compDurationType === 'months'
                      ? 'Between 1 and 240 months'
                      : 'Between 1 and 20 years'}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Reason (optional)
                </label>
                <textarea
                  value={compReason}
                  onChange={(e) => setCompReason(e.target.value)}
                  rows={3}
                  maxLength={200}
                  placeholder="e.g., Grant recipient, promotional offer, hardship waiver..."
                  className="w-full rounded-soft border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 resize-none"
                />
                <p className="mt-1 text-xs text-neutral-500">
                  This reason will appear in the user's notification email and audit log.
                </p>
              </div>

              <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
                {compDurationType === 'lifetime'
                  ? `${compUser.firstName} will receive free lifetime access to the "${compPlan}" plan with no expiration.`
                  : `${compUser.firstName} will receive ${compDurationAmount} ${compDurationType} of free access to the "${compPlan}" plan. If they already have a paid plan, time will be added to their existing period.`}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={closeCompModal} disabled={compSaving}>
                Cancel
              </Button>
              <Button
                onClick={handleGrantComplimentary}
                disabled={compSaving || (compDurationType !== 'lifetime' && compDurationAmount < 1)}
              >
                {compSaving ? 'Granting...' : 'Grant Complimentary Access'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminTiers;
